// frontend/src/hooks/useVendorSuppliers.js
//
// "Create from Shopify vendors" on the Supplier setup page: start the run,
// then watch it.
//
// The run makes each Shopify vendor's supplier the default of that vendor's
// items, over the whole catalog, so it runs in the background (backend:
// api/services/catalog_derivations.py adopt_vendor_suppliers). POST
// /api/suppliers/from-vendors/ answers 202 as soon as the run is QUEUED, and
// its counters arrive through GET /api/suppliers/from-vendors/status/.

import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { vendorSuppliers as vendorSuppliersKey } from '../queries/keys';
import { useApiMutation } from '../queries/useApiMutation';
import { useBackgroundRunStatus } from './useBackgroundRunStatus';

/**
 * Queue the run. Resolves with `{outcome, run_id, in_progress, ...}`;
 * `outcome` is `queued`, `queue_unavailable` (the watchdog re-sends it —
 * still in progress) or `already_running` (the run in flight is the one
 * returned). The answer is the status endpoint's state plus `outcome`
 * (backend: request_vendor_suppliers), so it becomes the cached status and
 * the watcher picks the run up now.
 */
export function useVendorSuppliersStart() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const queryClient = useQueryClient();

  return useApiMutation({
    mutationFn: () => apiJson.post('/api/suppliers/from-vendors/', {}),
    // Nothing has changed yet; the finished run announces the change.
    changes: [],
    onSuccess: (data) => queryClient.setQueryData(vendorSuppliersKey.status(storeId), data),
  });
}

/**
 * Poll the store's latest run while `enabled`. A finished run has written
 * new suppliers and new default relations, which move the items' costs.
 */
export function useVendorSuppliersStatus({ enabled = true } = {}) {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  return useBackgroundRunStatus({
    queryKey: vendorSuppliersKey.status(storeId),
    url: '/api/suppliers/from-vendors/status/',
    changesOnFinish: [DATA.suppliers, DATA.supplierLinks, DATA.items],
    enabled: enabled && !!storeId,
  });
}
